import {
  buildDataset,
  type ExportColumn,
  type ExportDataset,
  type ExportMetadata,
} from "@fleet/export";

/**
 * K1 adapter for the mail domain: maps message headers into the generic
 * {@link ExportDataset} for a metadata CSV (from/to/subject/date/folder).
 *
 * This is the DOM-free, entity-specific piece (parallel to `todo-adapter`).
 * Structural typing: any message-summary shape with these fields works, so the
 * web can pass its `MessageSummary[]` straight through. The CSV kit neutralizes
 * spreadsheet-formula injection on these text cells (see `csv.ts`), which
 * matters here because subjects and display names are attacker-controlled.
 */

/** The minimal message shape this adapter reads (a superset is fine). */
export interface MailExportRecord {
  from: string;
  to: string[];
  subject: string;
  date: string | null;
  folder: string;
}

export const MAIL_EXPORT_COLUMNS: ExportColumn<MailExportRecord>[] = [
  { key: "from", header: "From", accessor: (m) => m.from },
  { key: "to", header: "To", accessor: (m) => m.to.join(", ") },
  { key: "subject", header: "Subject", accessor: (m) => m.subject },
  { key: "date", header: "Date", accessor: (m) => m.date ?? "" },
  { key: "folder", header: "Folder", accessor: (m) => m.folder },
];

/**
 * Builds an {@link ExportDataset} of message metadata, ready for `datasetToCsv`.
 * `title`/`subtitle` land in the metadata (folder name + account email).
 */
export function buildMailMetadataDataset(
  messages: readonly MailExportRecord[],
  metadata: ExportMetadata = {},
): ExportDataset {
  return buildDataset(MAIL_EXPORT_COLUMNS, messages, metadata);
}
